import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'club_admin' | 'system_admin';
  club_id: string | null;
}

interface AdminAuthContextType {
  user: User | null;
  session: Session | null;
  adminUser: AdminUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAdminUser = async (userEmail: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', userEmail)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching admin user:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in fetchAdminUser:', error);
      return null;
    }
  };

  useEffect(() => {
    console.log('🔧 AdminAuth: Setting up auth listener');
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔧 AdminAuth: Auth state changed', { event, hasSession: !!session, email: session?.user?.email });
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user?.email) {
          console.log('🔧 AdminAuth: User logged in, fetching admin data for:', session.user.email);
          // Defer async operations to prevent auth state deadlocks
          setTimeout(async () => {
            try {
              const adminData = await fetchAdminUser(session.user.email);
              console.log('🔧 AdminAuth: Admin data fetched:', adminData);
              setAdminUser(adminData);
              setLoading(false);
            } catch (error) {
              console.error('🔧 AdminAuth: Error fetching admin data:', error);
              setAdminUser(null);
              setLoading(false);
            }
          }, 0);
        } else {
          console.log('🔧 AdminAuth: No user, clearing admin data');
          setAdminUser(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    console.log('🔧 AdminAuth: Checking for existing session');
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔧 AdminAuth: Initial session check', { hasSession: !!session, email: session?.user?.email });
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user?.email) {
        console.log('🔧 AdminAuth: Initial session found, fetching admin data for:', session.user.email);
        // Use setTimeout to defer async operations here too
        setTimeout(async () => {
          try {
            const adminData = await fetchAdminUser(session.user.email);
            console.log('🔧 AdminAuth: Initial admin data fetched:', adminData);
            setAdminUser(adminData);
            setLoading(false);
          } catch (error) {
            console.error('🔧 AdminAuth: Error fetching initial admin data:', error);
            setAdminUser(null);
            setLoading(false);
          }
        }, 0);
      } else {
        console.log('🔧 AdminAuth: No initial session found');
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/admin`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const isAdmin = !!adminUser;
  const isSuperAdmin = adminUser?.role === 'super_admin';

  const value = {
    user,
    session,
    adminUser,
    loading,
    signIn,
    signUp,
    signOut,
    isAdmin,
    isSuperAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}